import type { DashboardResponseDto, DashboardTraitDto } from "../types/dashboard";
import type { MachineChartsResponse } from "../types/machine-charts";
import { http, API_BASE } from "./http";

export type GetMachineTelemetryParams = {
  metric: string;
  from: string;
  to: string;
  bucket: string;
};

export type KitchenAnalyticsResponse = {
  avgCookingTimeSec: number | null;
  totalEnergyKwh: number | null;
  usageHours: number | null;
  cyclesToNextMaintenance: number | null;
  totalCycles: number | null;
  avgTemperature: number | null;
  programUsage: Array<{ programName: string; count: number }>;
  notifications: {
    ovenOffline: string[];
    errorActive: string[];
    errorActiveChamberIds?: number[];
    programFinished: Array<{ machineName: string; programName: string }>;
  };
  from: string;
  to: string;
};

export type RationalHaccpEntry = {
  id: number;
  batchId?: number;
  name?: string;
  appVersion?: string;
  startDate?: string;
  tempUnit?: string;
  recipeId?: number;
  category?: number;
  categoryImage?: string;
  chamberId?: number;
  deviceName?: string;
  deviceSerialNumber?: string;
  customerId?: number;
  customerName?: string;
  finished?: boolean;
  careRecipe?: boolean;
  energyConsumption?: string;
  features?: string[];
  groupId?: number;
  groupName?: string;
  duration?: number;
  openDoorCount?: number;
  raw?: any;
};

export type RationalDeviceSnapshot = {
  id: number;
  deviceId: number;
  name?: string | null;
  serialNumber?: string | null;
  groupName?: string | null;
  companyName?: string | null;
  deviceStatus?: string | null;
  chamberDefaultImageUrl?: string | null;
  chamberDefaultThumbnailUrl?: string | null;
  cookingProgram?: string | null;
  cookingProgramStep?: string | null;
  userActionRequired?: boolean | null;
  deviceErrorSupport?: boolean | null;
  deviceFamilyId?: number | null;
  deviceFamily?: string | null;
  deviceModel?: string | null;
  currentErrors?: any[];
  applicationVersion?: string | null;
  scriptVersion?: string | null;
  createdDate?: string | null;
  chamberTemperature?: string | null;
  foodTemperature?: string | null;
  humidity?: string | null;
  cleaningPercentage?: number | null;
  maintenancePercentage?: number | null;
  doorStatus?: string | null;
  timeLeft?: string | number | null;
  deviceIp?: string | null;
  deviceMac?: string | null;
  connectionType?: string | null;
  constructionMonth?: number | null;
  constructionYear?: number | null;
  availableFirmware?: Record<string, unknown> | null;
  deviceActions?: string[];
  deviceErrors?: boolean | null;
  confirmDeviceErrorSupport?: boolean | null;
  sensorLimits?: boolean | null;
  deviceRotated?: boolean | null;
  deviceRotation?: string | null;
  editable?: boolean | null;
  smartModeIcon?: string | null;
  isUnregistered?: boolean | null;
  isSensor?: boolean | null;
  isGateway?: boolean | null;
  simulated?: boolean | null;
  alarmDelayInSeconds?: number | null;
  alarmDisabled?: boolean | null;
  alarmDisabledByPausePeriod?: boolean | null;
  alarmDisabledByWeekDayAlarmOffsets?: boolean | null;
  alarmEnabledWeekdays?: unknown[];
  updateSeverity?: string | null;
  certificateDownload?: boolean | null;
  deviceFamilySupportsScriptStatus?: boolean | null;
  deviceFirmwareSupportsScriptStatus?: boolean | null;
  unregistered?: boolean | null;
  gateway?: boolean | null;
  sensor?: boolean | null;
  location?: string | null;
  liveSource?: string | null;
  raw?: any;
};

function extractMachineItems(dash: DashboardResponseDto | any): any[] {
  const md = (dash as any)?.machinesDashboard;

  if (Array.isArray(md?.items)) return md.items;
  if (Array.isArray(md)) return md;
  if (Array.isArray(md?.machines)) return md.machines;
  if (Array.isArray(md?.rows)) return md.rows;
  if (Array.isArray((dash as any)?.machines)) return (dash as any).machines;
  if (Array.isArray((dash as any)?.items)) return (dash as any).items;

  return [];
}

export const dashboardApi = {
  getDashboard: (token: string | null) =>
    http<DashboardResponseDto>(`/dashboard`, { method: "GET" }, token),

  getMachineTelemetry: (
    token: string | null,
    machineId: string,
    params: GetMachineTelemetryParams
  ) => {
    const query = new URLSearchParams({
      metric: params.metric,
      from: params.from,
      to: params.to,
      bucket: params.bucket,
    }).toString();

    return http<any>(
      `/machines/${machineId}/telemetry?${query}`,
      { method: "GET" },
      token
    );
  },

  // ✅ Grafici completi macchina (widgets)
  // GET /dashboard/machines/:machineId/charts?from=...&to=...
  getMachineCharts: (
    token: string | null,
    machineId: string,
    fromISO?: string,
    toISO?: string
  ) => {
    const qs = new URLSearchParams();
    if (fromISO) qs.set("from", fromISO);
    if (toISO) qs.set("to", toISO);

    const q = qs.toString();
    const url = q
      ? `/dashboard/machines/${machineId}/charts?${q}`
      : `/dashboard/machines/${machineId}/charts`;

    return http<MachineChartsResponse>(url, { method: "GET" }, token);
  },

  /** Live da DB: TelemetryLatest (con raw), Alarm, AlarmOccurrence. OPC UA sync + stream. */
  getMachineLive: (token: string | null, machineId: string) =>
    http<{
      telemetryLatest: Array<{
        metric: string;
        value: number;
        unit: string | null;
        ts: string;
        raw: Record<string, unknown> | null;
      }>;
      alarms: Array<{
        id: string;
        code: string;
        message: string | null;
        severity: string;
        occurredAt: string;
        acknowledgedAt: string | null;
      }>;
      alarmOccurrences: Array<{
        code: string;
        message: string | null;
        severity: string;
        occurredAt: string;
      }>;
    }>(`/dashboard/machines/${encodeURIComponent(machineId)}/live`, { method: "GET" }, token),

  /** Analytics cucina: KPI, cicli, consumo, manutenzione, notifiche */
  getKitchenAnalytics: (
    token: string | null,
    params?: { from?: string; to?: string; machineId?: string }
  ) => {
    const qs = new URLSearchParams();
    if (params?.from) qs.set("from", params.from);
    if (params?.to) qs.set("to", params.to);
    if (params?.machineId) qs.set("machineId", params.machineId);
    const q = qs.toString();
    const url = q ? `/dashboard/kitchen/analytics?${q}` : "/dashboard/kitchen/analytics";
    return http<KitchenAnalyticsResponse>(url, { method: "GET" }, token);
  },

  getMachinesByProvider: async (token: string | null, provider: string) => {
    const dash = await dashboardApi.getDashboard(token);
    const items = dash.machinesDashboard?.items ?? extractMachineItems(dash);
    const p = String(provider).toUpperCase();
    return items.filter((m: any) => String(m.provider).toUpperCase() === p);
  },

  getMultiparametric: () =>
    http<{
      status: "ok" | "degraded" | "down";
      params: Array<{ label: string; value: number; unit: string; ok: boolean }>;
      lastUpdate: string;
    }>("/dashboard/multiparametric", { method: "GET" }),

  getOperationalNotes: () =>
    http<{
      lineConfig: { name: string; username: string; note: string };
      serverIPs: Array<{ address: string; family: string; internal: boolean }>;
      machines: Array<{
        id: string;
        name: string;
        provider: string;
        status: string;
        externalId: string | null;
        lastSeenAt: string | null;
        ip?: string;
      }>;
    }>("/dashboard/operational-notes", { method: "GET" }),

  getIrinoxEnergyChart: (
    _token: string | null,
    machineId: string,
    startTimestampMs?: number
  ) => {
    const qs = startTimestampMs != null
      ? `?startTimestamp=${startTimestampMs}`
      : "";
    return http<{ series: Array<{ label: string; data: number[] }>; labels: string[] }>(
      `/dashboard/irinox/machines/${encodeURIComponent(machineId)}/energy-chart${qs}`,
      { method: "GET" }
    );
  },

  /** Energia aggregata Irinox (tutti i dispositivi) */
  getIrinoxEnergyAggregate: (
    _token: string | null,
    startTimestampMs?: number,
    accountId?: string
  ) => {
    const qs = new URLSearchParams();
    if (startTimestampMs != null) qs.set("startTimestamp", String(startTimestampMs));
    if (accountId) qs.set("accountId", accountId);
    const query = qs.toString();
    return http<{ series: Array<{ label: string; data: number[] }>; labels: string[] }>(
      `/dashboard/irinox/energy/aggregate${query ? `?${query}` : ""}`,
      { method: "GET" }
    );
  },

  /** Consumo, spesa, CO2 per singolo appliance Irinox */
  getIrinoxEnergyAppliances: (
    _token: string | null,
    startTimestampMs?: number,
    accountId?: string
  ) => {
    const qs = new URLSearchParams();
    if (startTimestampMs != null) qs.set("startTimestamp", String(startTimestampMs));
    if (accountId) qs.set("accountId", accountId);
    const query = qs.toString();
    return http<Array<{
      serialNumber: string;
      name?: string;
      consumption?: number;
      expense?: number;
      co2?: number;
      labName?: string;
      role?: string;
    }>>(
      `/dashboard/irinox/energy/appliances${query ? `?${query}` : ""}`,
      { method: "GET" }
    );
  },

  /** Profilo / impostazioni account Irinox (FreshCloud) */
  getIrinoxUserInfo: (_token: string | null, accountId?: string) => {
    const qs = new URLSearchParams();
    if (accountId) qs.set("accountId", accountId);
    const query = qs.toString();
    return http<{
      country?: string;
      firstName?: string | null;
      lastName?: string | null;
      companyName?: string | null;
      email?: string | null;
      language?: string | null;
      brand?: string | null;
      role?: string | null;
      status?: string | null;
      preferences?: Record<string, any>;
    }>(`/dashboard/irinox/user-info${query ? `?${query}` : ""}`, { method: "GET" });
  },

  /** Dettaglio ricetta / programma Irinox per una macchina */
  getIrinoxRecipe: (
    _token: string | null,
    machineId: string,
    recipeId: string
  ) =>
    http<{
      id: string;
      name: string;
      description?: string;
      temperature?: number | null;
      type?: number | null;
      function?: number | null;
      numPhases?: number | null;
      numRuns?: number | null;
      sector?: number | null;
      createdAt?: number | null;
      raw?: any;
    }>(
      `/dashboard/irinox/machines/${encodeURIComponent(
        machineId
      )}/recipes/${encodeURIComponent(recipeId)}`,
      { method: "GET" }
    ),

  /** Dettaglio Irinox: stato, info, allarmi (da API Irinox) */
  getIrinoxMachineDetail: (_token: string | null, machineId: string) =>
    http<{
      stato: { online: boolean; cycleRunning: number; type: string; domain: string };
      info: Record<string, unknown>;
      alarms: Array<{ name: string; timestamp: number; active: boolean }>;
      status: Record<string, unknown>;
      cycle: Record<string, unknown> | null;
      deviceTwin: { info: any; status: any; versions: any } | null;
    }>(`/dashboard/irinox/machines/${encodeURIComponent(machineId)}/detail`, { method: "GET" }),

  /** Dettaglio Rational: stato, temperature, umidità, porta, cura/pulizia, programma, errori */
  getRationalMachineDetail: (_token: string | null, machineId: string) =>
    http<{
      info: Record<string, unknown>;
      stato: Record<string, unknown>;
      temperature: Record<string, unknown>;
      doorStatus: string;
      cleaningMaintenance: Record<string, unknown>;
      program: Record<string, unknown>;
      errors: Array<{ errorCode: string; errorCodeTranslation?: string; active: boolean; startTime?: string }>;
    }>(`/dashboard/rational/machines/${encodeURIComponent(machineId)}/detail`, { method: "GET" }),

  /** Storico errori dispositivo Rational (lista da /api/devices/{deviceId}/errors) */
  getRationalDeviceErrors: (_token: string | null, machineId: string, size?: number) => {
    const qs = size ? `?size=${size}` : "";
    return http<{
      items: Array<{
        id: number;
        errorCode: string;
        errorCodeTranslation?: string;
        errorType?: string;
        active: boolean;
        startTime?: string;
        endTime?: string;
        deviceId: number;
      }>;
      accessDenied: boolean;
    }>(
      `/dashboard/rational/machines/${encodeURIComponent(machineId)}/errors-history${qs}`,
      { method: "GET" }
    );
  },

  /** Dashboard Rational (Connected Cooking): widget stato errore/online, consumi, programmi, ecc. */
  getRationalDashboard: (_token: string | null, accountId?: string) => {
    const qs = accountId ? `?accountId=${encodeURIComponent(accountId)}` : "";
    return http<{ dashboards: any[]; config: any[] }>(`/dashboard/rational/dashboard${qs}`, { method: "GET" });
  },

  getRationalDevices: (_token: string | null, accountId?: string) => {
    const qs = accountId ? `?accountId=${encodeURIComponent(accountId)}` : "";
    return http<{ groupName?: string; items: RationalDeviceSnapshot[] }>(
      `/dashboard/rational/devices${qs}`,
      { method: "GET" }
    );
  },

  getRationalDeviceDetail: (
    _token: string | null,
    chamberId: number | string,
    accountId?: string
  ) => {
    const qs = accountId ? `?accountId=${encodeURIComponent(accountId)}` : "";
    return http<RationalDeviceSnapshot>(
      `/dashboard/rational/devices/${encodeURIComponent(String(chamberId))}/detail${qs}`,
      { method: "GET" }
    );
  },

  /** Protocolli HACCP Rational per gruppo/data. filter: 'care' | 'cooking' | undefined (tutti) */
  getRationalHaccp: (
    _token: string | null,
    params?: { from?: string; to?: string; size?: number; filter?: string; accountId?: string }
  ) => {
    const qs = new URLSearchParams();
    if (params?.from) qs.set("from", params.from);
    if (params?.to) qs.set("to", params.to);
    if (params?.size != null) qs.set("size", String(params.size));
    if (params?.filter) qs.set("filter", params.filter);
    if (params?.accountId) qs.set("accountId", params.accountId);
    const query = qs.toString();
    return http<{
      groupId?: number;
      groupName?: string;
      from?: string;
      to?: string;
      items: RationalHaccpEntry[];
    }>(`/dashboard/rational/haccp${query ? `?${query}` : ""}`, { method: "GET" });
  },

  getRationalHaccpDetail: (
    _token: string | null,
    haccpId: number | string,
    params?: { incomplete?: boolean; accountId?: string }
  ) => {
    const qs = new URLSearchParams();
    if (params?.incomplete != null) qs.set("incomplete", String(params.incomplete));
    if (params?.accountId) qs.set("accountId", params.accountId);
    const query = qs.toString();
    return http<RationalHaccpEntry>(
      `/dashboard/rational/haccp/${encodeURIComponent(String(haccpId))}${query ? `?${query}` : ""}`,
      { method: "GET" }
    );
  },

  /** Admin chart Irinox (es. state_1) per singola macchina */
  getIrinoxAdminChart: (
    _token: string | null,
    machineId: string,
    chartId: string,
    fromISO: string,
    toISO: string,
    intervalSec = 3600,
    toleranceSec = 900
  ) => {
    const startDate = new Date(fromISO).toISOString().slice(0, 10);
    const endDate = new Date(toISO).toISOString().slice(0, 10);
    const qs = new URLSearchParams({
      startDate,
      endDate,
      interval: String(intervalSec),
      tolerance: String(toleranceSec),
    }).toString();

    return http<import("./types").ChartWidget>(
      `/dashboard/irinox/machines/${encodeURIComponent(machineId)}/admin-chart/${encodeURIComponent(chartId)}?${qs}`,
      { method: "GET" }
    );
  },

  /** Dashboard Elettrobar: lavaggi/giorno, notifiche, tabella cicli */
  getElettrobarDashboard: (token: string | null, machineId?: string) => {
    const qs = machineId ? `?machineId=${encodeURIComponent(machineId)}` : "";
    return http<{
      washesPerDay: Array<{ day: string; lavaggi: number; frequenza?: number }>;
      washesPerHour?: Array<{ ora: number; frequenza: number }>;
      totalWashes: number;
      maintenanceAlerts: Array<{
        machineId: string;
        machineName: string;
        totalCycles: number;
        nextAt: number;
        remaining: number;
        type: string;
      }>;
      machines: Array<{ id: string; name: string; totalCycles: number }>;
      cycles?: Array<{
        id: string;
        serialNumber: string;
        model: string;
        machineName: string;
        timestamp: string;
        cycleCounter: string;
        program: string;
        result: string;
        washTime: string | number;
        rinseTime: string | number;
        cycleTime: string | number;
        initialWashTemp: string | number;
        initialRinseTemp: string | number;
        alarm: string;
        a0: string;
      }>;
    }>(`/elettrobar/dashboard${qs}`, { method: "GET" }, token);
  },

  /** Imposta macchine Elettrobar dal CSV (estrae seriali e crea le macchine) */
  setupElettrobarMachines: (token: string | null, csv: string) =>
    http<{ created: number; updated: number; serials: string[] }>(
      "/elettrobar/setup-machines",
      { method: "POST", body: JSON.stringify({ csv }) },
      token
    ),

  /** Cancella tutti i dati Elettrobar (cicli + contatori) */
  deleteElettrobarData: (token: string | null) =>
    http<{ deletedCycles: number; resetCounters: number }>("/elettrobar/data", { method: "DELETE" }, token),

  /** Cancella tutte le macchine Elettrobar (cicli, contatori, macchine) */
  deleteElettrobarMachines: (token: string | null) =>
    http<{ deletedMachines: number; deletedCycles: number }>("/elettrobar/machines", { method: "DELETE" }, token),

  /** Inserisce macchina Elettrobar (nome + seriale) */
  createElettrobarMachine: (token: string | null, name: string, serialNumber: string) =>
    http<{ id: string; name: string }>(
      "/elettrobar/machines",
      { method: "POST", body: JSON.stringify({ name, serialNumber }) },
      token
    ),

  /** Registra manutenzione effettuata su macchina Elettrobar */
  recordElettrobarMaintenance: (token: string | null, machineId: string) =>
    http<{ ok: boolean; message: string }>(
      `/elettrobar/maintenance/${encodeURIComponent(machineId)}`,
      { method: "POST" },
      token
    ),

  /** Importa CSV cicli Elettrobar (createIfMissing: crea macchine se non esistono) */
  importElettrobarCsv: (token: string | null, csv: string, createIfMissing?: boolean) =>
    http<{ imported: number; skipped: number; errors: string[]; created?: number }>(
      "/elettrobar/import",
      { method: "POST", body: JSON.stringify({ csv, createIfMissing: !!createIfMissing }) },
      token
    ),

  /** Sincronizza CSV da Gmail e importa automaticamente */
  syncElettrobarFromGmail: (
    token: string | null,
    credentials: { clientId: string; clientSecret: string; refreshToken: string; email: string },
    createIfMissing?: boolean
  ) =>
    http<{
      success: boolean;
      message: string;
      filename?: string;
      date?: string;
      imported: number;
      skipped: number;
      errors?: string[];
    }>(
      "/elettrobar/sync-gmail",
      {
        method: "POST",
        body: JSON.stringify({ ...credentials, createIfMissing: !!createIfMissing }),
      },
      token
    ),

  /** Testa connessione Gmail e mostra email con CSV */
  testElettrobarGmail: (
    token: string | null,
    credentials: { clientId: string; clientSecret: string; refreshToken: string; email: string }
  ) =>
    http<{
      success: boolean;
      message?: string;
      count: number;
      emails: Array<{
        subject: string;
        date: string;
        from: string;
        attachments: Array<{ filename: string; size: number }>;
      }>;
    }>(
      "/elettrobar/gmail/test",
      { method: "POST", body: JSON.stringify(credentials) },
      token
    ),

  /** Ultimo report lavaggi scaricato da Gmail (tabella in JSON). Aggiornato una volta al giorno dal cron. */
  getElettrobarReportLatest: (token: string | null) =>
    http<{ rows: Record<string, unknown>[]; filename: string; date: string; message?: string }>(
      "/elettrobar/report-latest",
      { method: "GET" },
      token
    ),

  /** Scarica ora il report da Gmail e mettilo in cache (email es. lamediazione.coop@gmail.com) */
  fetchElettrobarReport: (
    token: string | null,
    credentials: { clientId: string; clientSecret: string; refreshToken: string; email: string }
  ) =>
    http<{ success: boolean; rows: number; filename: string; date: string; message?: string }>(
      "/elettrobar/report-fetch",
      { method: "POST", body: JSON.stringify(credentials) },
      token
    ),

  /**
   * Avvia il download del file report lavaggi (Excel/CSV) dalla cache.
   * Richiede token; il file viene scaricato con il nome originale.
   */
  downloadElettrobarReport: async (token: string | null, filenameHint?: string): Promise<void> => {
    const res = await fetch(`${API_BASE}/elettrobar/report-latest/download`, {
      method: "GET",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(res.status === 404 ? "Nessun report in cache" : `HTTP ${res.status}`);
    const disposition = res.headers.get("Content-Disposition");
    const match = disposition?.match(/filename="?([^";]+)"?/);
    const filename = match?.[1]?.trim() || filenameHint || "report-lavaggi.xlsx";
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },

  /** Traits disponibili per i widget dashboard Rational (types, timePeriods, filters, unit) */
  getRationalDashboardTraits: (_token: string | null, accountId?: string) => {
    const qs = accountId ? `?accountId=${encodeURIComponent(accountId)}` : "";
    return http<DashboardTraitDto[]>(`/dashboard/rational/traits${qs}`, { method: "GET" });
  },

  getVendorOverview: async (token: string | null, provider: string) => {
    const p = String(provider).toUpperCase();
    const [dash, energyRes] = await Promise.all([
      dashboardApi.getDashboard(token),
      http<Array<{ day: string; kwh: number }>>(
        `/dashboard/vendors/${encodeURIComponent(p)}/energy?days=7`,
        { method: "GET" },
        token
      ).catch(() => []),
    ]);

    const items = dash.machinesDashboard?.items ?? extractMachineItems(dash);
    const vendorMachines = items.filter(
      (m: any) => String(m.provider).toUpperCase() === p
    );

    const machinesCount = vendorMachines.length;

    const zanardelliCount = vendorMachines.filter((m: any) => {
      const loc = String(m.location ?? m.machineLocation ?? "").toLowerCase();
      const name = String(m.name ?? "").toLowerCase();
      return loc.includes("zanardelli") || name.includes("zanardelli");
    }).length;

    const onlineCount = vendorMachines.filter(
      (m: any) => String(m.status).toUpperCase() === "ONLINE"
    ).length;

    const alarmCount = vendorMachines.filter(
      (m: any) => String(m.status).toUpperCase() === "ALARM"
    ).length;

    const alarmsLast24h = vendorMachines.reduce(
      (sum: number, m: any) => sum + Number(m.alarmsLast24hCount ?? 0),
      0
    );

    const alarmsByMachine = vendorMachines
      .filter((m: any) => Number(m.alarmsActive ?? 0) > 0)
      .map((m: any) => ({
        name: m.name ?? m.machineName ?? m.id,
        value: Number(m.alarmsActive ?? 0),
      }));

    // Fallback: se nessun allarme attivo, mostra distribuzione ultime 24h
    const alarmsForChart =
      alarmsByMachine.length > 0
        ? alarmsByMachine
        : vendorMachines
            .filter((m: any) => Number(m.alarmsLast24hCount ?? 0) > 0)
            .map((m: any) => ({
              name: m.name ?? m.machineName ?? m.id,
              value: Number(m.alarmsLast24hCount ?? 0),
      }));

    return {
      machinesCount,
      zanardelliCount,
      onlineCount,
      alarmCount,
      alarmsLast24h,
      alarmsByMachine: alarmsForChart,
      vendorMachines,
      energyLast7d: Array.isArray(energyRes) ? energyRes : [],
      alarmsByCode: alarmsForChart,
    };
  },
};